import { useEffect, useMemo, useState } from 'react';
import { AlertOctagon, Braces, Copy, FileJson, MapPin, MessageSquareText, Wrench } from 'lucide-react';
import type { JsonValue } from '@maland/contracts';
import { formatDuration, formatNumber, prettyJson } from '../lib/format';
import type { ModelHttpRequestDetail } from '../types';
import { JsonInspector } from './JsonInspector';
import { StatusPill } from './StatusPill';

type BodyTab = 'prompt' | 'request' | 'response' | 'tools';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export interface ModelToolNameTag {
  readonly name: string;
  readonly callId?: string;
  readonly source: 'direct' | 'correlated' | 'unknown_legacy';
}

export interface ModelMessageView {
  readonly message: Record<string, unknown>;
  readonly toolNames: readonly ModelToolNameTag[];
}

interface ParsedToolCall {
  readonly callId?: string;
  readonly name?: string;
}

function nonEmptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}

function parsedToolCalls(message: Record<string, unknown>): ParsedToolCall[] {
  if (!Array.isArray(message.tool_calls)) return [];
  return message.tool_calls.flatMap((candidate) => {
    if (!isRecord(candidate)) return [];
    const fn = isRecord(candidate.function) ? candidate.function : undefined;
    const callId = nonEmptyString(candidate.id) ?? nonEmptyString(candidate.call_id);
    const name = nonEmptyString(fn?.name) ?? nonEmptyString(candidate.name);
    return [{ ...(callId === undefined ? {} : { callId }), ...(name === undefined ? {} : { name }) }];
  });
}

/**
 * Resolves tool-result names only from protocol identity:
 *   1. an explicit result message `name`; or
 *   2. its `tool_call_id` matched to a preceding assistant `tool_calls[].id`.
 *
 * Duplicate call IDs with conflicting names are deliberately made
 * unresolvable. Adjacency, result summaries and tool catalog order are never
 * used, so legacy data cannot be silently mislabeled.
 */
export function resolveModelMessageViews(messages: readonly Record<string, unknown>[]): ModelMessageView[] {
  const namesByCallId = new Map<string, string>();
  const conflictedCallIds = new Set<string>();

  return messages.map((message) => {
    const calls = parsedToolCalls(message);
    const callTags = calls.map((call): ModelToolNameTag => ({
      name: call.name ?? 'unknown/legacy',
      ...(call.callId === undefined ? {} : { callId: call.callId }),
      source: call.name === undefined ? 'unknown_legacy' : 'direct',
    }));

    for (const call of calls) {
      if (call.callId === undefined || call.name === undefined) continue;
      const previous = namesByCallId.get(call.callId);
      if (previous !== undefined && previous !== call.name) {
        namesByCallId.delete(call.callId);
        conflictedCallIds.add(call.callId);
      } else if (!conflictedCallIds.has(call.callId)) {
        namesByCallId.set(call.callId, call.name);
      }
    }

    if (message.role !== 'tool') return { message, toolNames: callTags };

    const callId = nonEmptyString(message.tool_call_id) ?? nonEmptyString(message.call_id);
    const directName = nonEmptyString(message.name);
    const correlatedName = callId !== undefined && !conflictedCallIds.has(callId)
      ? namesByCallId.get(callId)
      : undefined;
    return {
      message,
      toolNames: [{
        name: directName ?? correlatedName ?? 'unknown/legacy',
        ...(callId === undefined ? {} : { callId }),
        source: directName !== undefined
          ? 'direct'
          : correlatedName !== undefined
            ? 'correlated'
            : 'unknown_legacy',
      }],
    };
  });
}

export function modelMessageLabel(message: Record<string, unknown>, index: number): string {
  return typeof message.role === 'string' ? message.role : `message ${index + 1}`;
}

export function modelToolOutcome(message: Record<string, unknown>): Record<string, unknown> | undefined {
  if (message.role !== 'tool') return undefined;
  let content: unknown = message.content;
  if (typeof content === 'string') {
    try { content = JSON.parse(content); } catch { return undefined; }
  }
  if (!isRecord(content)) return undefined;
  const outcome: Record<string, unknown> = {};
  for (const key of ['status', 'applied', 'authoritative', 'handled', 'ok'])
    if (content[key] !== undefined) outcome[key] = content[key];
  return Object.keys(outcome).length ? outcome : undefined;
}

function MessageHeader({ view, index }: { view: ModelMessageView; index: number }) {
  const { message, toolNames } = view;
  const outcome = modelToolOutcome(message);
  return (
    <header className="prompt-message__header">
      <span className="prompt-message__identity">
        <span>{modelMessageLabel(message, index)}</span>
        {toolNames.map((tool, toolIndex) => (
          <code
            className={`prompt-message__tool-name prompt-message__tool-name--${tool.source}`}
            key={`${tool.callId ?? 'no-call-id'}-${toolIndex}`}
            title={`${tool.callId === undefined ? 'call_id unavailable' : `call_id=${tool.callId}`} · ${tool.source}`}
          >
            {tool.name}
          </code>
        ))}
      </span>
      {outcome && <span className="prompt-message__outcome">
        {Object.entries(outcome).map(([key, value]) => (
          <code className={`prompt-message__chip prompt-message__chip--${key}-${String(value)}`} key={key}>
            {key}={String(value)}
          </code>
        ))}
      </span>}
    </header>
  );
}

function CopyPayloadButton({ value }: { value: unknown }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard?.writeText(prettyJson(value));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };
  return <button className="icon-button" onClick={() => void copy()}><Copy size={14} />{copied ? '已复制' : '复制'}</button>;
}

function PromptStructure({ requestBody }: Pick<ModelHttpRequestDetail, 'requestBody'>) {
  const body = isRecord(requestBody) ? requestBody : {};
  const messagesValue = Array.isArray(body.messages) ? body.messages : Array.isArray(body.input) ? body.input : [];
  const messages = messagesValue.filter(isRecord);
  const messageViews = resolveModelMessageViews(messages);
  const systemMessages = messageViews.filter(({ message }) => message.role === 'system' || message.role === 'developer');
  const conversation = messageViews.filter(({ message }) => message.role !== 'system' && message.role !== 'developer');
  const tools = Array.isArray(body.tools) ? body.tools : [];
  const parameters = Object.fromEntries(Object.entries(body).filter(([key]) => !['messages', 'input', 'tools'].includes(key)));

  return (
    <div className="prompt-inspector">
      <div className="prompt-summary-grid">
        <div><span>Model</span><strong>{typeof body.model === 'string' ? body.model : '—'}</strong></div>
        <div><span>Stream</span><strong>{typeof body.stream === 'boolean' ? String(body.stream) : '—'}</strong></div>
        <div><span>Messages</span><strong>{messages.length}</strong></div>
        <div><span>Tools</span><strong>{tools.length}</strong></div>
      </div>
      <section className="prompt-section">
        <div className="subsection-title"><div><Braces size={15} /><strong>请求参数</strong></div><CopyPayloadButton value={parameters} /></div>
        <JsonInspector value={parameters} />
      </section>
      <section className="prompt-section">
        <div className="subsection-title"><div><MessageSquareText size={15} /><strong>System / Developer Prompt</strong></div><CopyPayloadButton value={systemMessages.map(({ message }) => message)} /></div>
        {systemMessages.length ? systemMessages.map((view, index) => (
          <article className="prompt-message prompt-message--system" key={index}>
            <MessageHeader view={view} index={index} />
            <pre>{typeof view.message.content === 'string' ? view.message.content : prettyJson(view.message.content)}</pre>
          </article>
        )) : <p className="muted">未发现 system/developer message；可在“原始发包 Body”中检查其他协议字段。</p>}
      </section>
      <section className="prompt-section">
        <div className="subsection-title"><div><MessageSquareText size={15} /><strong>对话消息</strong></div><CopyPayloadButton value={conversation.map(({ message }) => message)} /></div>
        <div className="prompt-message-list">
          {conversation.map((view, index) => (
            <article className="prompt-message" key={index}>
              <MessageHeader view={view} index={index} />
              <pre>{typeof view.message.content === 'string' ? view.message.content : prettyJson(view.message.content)}</pre>
            </article>
          ))}
          {!conversation.length && <p className="muted">没有可解析的对话消息。</p>}
        </div>
      </section>
      {!!tools.length && <section className="prompt-section">
        <div className="subsection-title"><div><Wrench size={15} /><strong>Tool 定义</strong></div><CopyPayloadButton value={tools} /></div>
        <JsonInspector value={tools} />
      </section>}
    </div>
  );
}

export function ModelRequestDetailView({ detail }: { detail: ModelHttpRequestDetail }) {
  const [tab, setTab] = useState<BodyTab>('prompt');
  useEffect(() => setTab('prompt'), [detail.id]);
  const responseLabel = useMemo(() => detail.responseTruncated ? '解析后回包 Body（已截断）' : '解析后回包 Body', [detail.responseTruncated]);
  const toolCallsJson = useMemo<JsonValue>(() => detail.toolCalls.map((call) => ({
    name: call.name,
    ...(call.arguments === undefined ? {} : { arguments: call.arguments }),
  })), [detail.toolCalls]);

  return (
    <div className="model-request-detail-view">
      <header className="detail-hero">
        <div className="detail-hero__topline"><span className="eyebrow">HTTP BODY INSPECTOR</span><StatusPill status={detail.status} /></div>
        <h2>Request #{detail.id}</h2>
        <div className="detail-hero__ids"><code>{detail.runId ?? 'unattributed'}</code><span>/</span><code>{detail.kind ?? 'unknown'}</code><span>/</span><code className="detail-hero__room"><MapPin size={10} />{detail.roomId ?? '未关联 Room'}</code></div>
        {detail.error && <div className="detail-alert"><AlertOctagon size={18} /><div><strong>模型请求异常</strong><p>{detail.error}</p></div></div>}
        <div className="detail-metrics"><div><span>请求字节</span><strong>{formatNumber(detail.requestBytes)}</strong></div><div><span>耗时</span><strong>{formatDuration(detail.durationMs)}</strong></div><div><span>Token</span><strong>{formatNumber(detail.tokenUsage.total)}</strong></div><div><span>Tool Calls</span><strong>{detail.toolCalls.length}</strong></div></div>
      </header>
      <div className="tabs body-tabs" role="tablist" aria-label="HTTP 包体视图">
        <button role="tab" aria-selected={tab === 'prompt'} onClick={() => setTab('prompt')}>Prompt 结构</button>
        <button role="tab" aria-selected={tab === 'request'} onClick={() => setTab('request')}>原始发包 Body</button>
        <button role="tab" aria-selected={tab === 'response'} onClick={() => setTab('response')}>解析后回包 Body</button>
        <button role="tab" aria-selected={tab === 'tools'} onClick={() => setTab('tools')}>Tool Calls <span>{detail.toolCalls.length}</span></button>
      </div>
      {tab === 'prompt' && <PromptStructure requestBody={detail.requestBody} />}
      {tab === 'request' && <section className="body-code-panel">
        <div className="subsection-title"><div><Braces size={15} /><strong>HTTP Request Body</strong></div><CopyPayloadButton value={detail.requestBody} /></div>
        {detail.requestTruncated && <div className="inline-error"><AlertOctagon size={15} />请求超过采集上限，当前内容已截断</div>}
        <JsonInspector value={detail.requestBody} />
      </section>}
      {tab === 'response' && <section className="body-code-panel">
        <div className="subsection-title"><div><FileJson size={15} /><strong>{responseLabel}</strong></div><CopyPayloadButton value={detail.responseBody} /></div>
        <div className="normalized-body-notice">由 Go 端解析结果重建，便于检查文本、工具调用、Token 与错误；不是模型网关的原始 SSE 传输流。</div>
        <JsonInspector value={detail.responseBody} />
      </section>}
      {tab === 'tools' && <section className="body-code-panel">
        <div className="subsection-title"><div><Wrench size={15} /><strong>Tool Calls</strong></div><CopyPayloadButton value={detail.toolCalls} /></div>
        <JsonInspector value={toolCallsJson} />
      </section>}
    </div>
  );
}
